import React, {
	createContext,
	useState,
	ReactNode,
	useLayoutEffect
} from 'react';
import { DynamicPageProps } from 'types/DynamicPageProps';
import {
	ElementDynamicOptionsProps,
	TypeSlug
} from 'types/ElementDynamicOptionProps';
import { ItemDynamicPageInPhone } from 'types/ItemDynamicPageInPhone';

interface DynamicPageContextProps {
	isLoad: boolean;
	setIsLoad: React.Dispatch<React.SetStateAction<boolean>>;
	isFormOpen: boolean;
	setIsFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
	dynamicPagesList: DynamicPageProps[];
	setDynamicPagesList: React.Dispatch<React.SetStateAction<DynamicPageProps[]>>;
	listOptions: ElementDynamicOptionsProps[]; //lista de módulos disponíveis para arrastar para dentro do celular
	setListOptions: React.Dispatch<
		React.SetStateAction<ElementDynamicOptionsProps[]>
	>;
	backupListOptions: ElementDynamicOptionsProps[];
	setBackupLisOptions: React.Dispatch<
		React.SetStateAction<ElementDynamicOptionsProps[]>
	>;
	dynamicPageSelected: DynamicPageProps | null;
	setDynamicPageSelected: React.Dispatch<
		React.SetStateAction<DynamicPageProps | null>
	>;
	itensOnPhone: ItemDynamicPageInPhone[]; //lista de módulo no telefone
	setItensOnPhone: React.Dispatch<
		React.SetStateAction<ItemDynamicPageInPhone[]>
	>;
	itensOnPhoneBackup: ItemDynamicPageInPhone[]; //lista de módulo no telefone backup
	setItensOnPhoneBackup: React.Dispatch<
		React.SetStateAction<ItemDynamicPageInPhone[]>
	>;
	typeItemToAdd: TypeSlug | null; //tipo (slug) do módulo que foi arrastado
	setTypeItemToAdd: React.Dispatch<React.SetStateAction<TypeSlug | null>>;
	moduleOnPhoneToEdit: ItemDynamicPageInPhone | null; //módulo que já esta salvo e será editado
	setModuleOnPhoneToEdit: React.Dispatch<
		React.SetStateAction<ItemDynamicPageInPhone | null>
	>;
	moduleInSavingMode: ItemDynamicPageInPhone | null; //módulo que ainda não foi salvo, este módulo acabou de ser colocado no celular.
	setModuleInSavingMode: React.Dispatch<
		React.SetStateAction<ItemDynamicPageInPhone | null>
	>;
	canSaveModuleOnBack: boolean;
	setCanSaveModuleOnBack: React.Dispatch<React.SetStateAction<boolean>>;
	messageErrorForm: string;
	setMessageErrorForm: React.Dispatch<React.SetStateAction<string>>;
}

const DynamicPageContext = createContext<DynamicPageContextProps>({
	isLoad: false,
	setIsLoad: () => {},
	isFormOpen: false,
	setIsFormOpen: () => {},
	dynamicPagesList: [],
	setDynamicPagesList: () => {},
	listOptions: [],
	setListOptions: () => {},
	backupListOptions: [],
	setBackupLisOptions: () => {},
	dynamicPageSelected: null,
	setDynamicPageSelected: () => {},
	itensOnPhone: [],
	setItensOnPhone: () => {},
	itensOnPhoneBackup: [],
	setItensOnPhoneBackup: () => {},
	typeItemToAdd: null,
	setTypeItemToAdd: () => {},
	moduleOnPhoneToEdit: null,
	setModuleOnPhoneToEdit: () => {},
	moduleInSavingMode: null,
	setModuleInSavingMode: () => {},
	canSaveModuleOnBack: false,
	setCanSaveModuleOnBack: () => {},
	messageErrorForm: '',
	setMessageErrorForm: () => {}
});

interface ContextProps {
	children: ReactNode;
}

export function DynamicPageProvider({ children }: ContextProps) {
	const [messageErrorForm, setMessageErrorForm] = useState('');
	const [isLoad, setIsLoad] = useState<boolean>(false);
	const [canSaveModuleOnBack, setCanSaveModuleOnBack] =
		useState<boolean>(false);
	const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
	const [dynamicPagesList, setDynamicPagesList] = useState<DynamicPageProps[]>(
		[]
	);
	const [dynamicPageSelected, setDynamicPageSelected] =
		useState<DynamicPageProps | null>(null);
	const [listOptions, setListOptions] = useState<ElementDynamicOptionsProps[]>(
		[]
	);
	const [backupListOptions, setBackupLisOptions] = useState<
		ElementDynamicOptionsProps[]
	>([]);
	const [itensOnPhone, setItensOnPhone] = useState<ItemDynamicPageInPhone[]>(
		[]
	);
	const [itensOnPhoneBackup, setItensOnPhoneBackup] = useState<
		ItemDynamicPageInPhone[]
	>([]);
	const [typeItemToAdd, setTypeItemToAdd] = useState<TypeSlug | null>(null);
	const [moduleOnPhoneToEdit, setModuleOnPhoneToEdit] =
		useState<ItemDynamicPageInPhone | null>(null);
	const [moduleInSavingMode, setModuleInSavingMode] =
		useState<ItemDynamicPageInPhone | null>(null);

	useLayoutEffect(() => {
		if (canSaveModuleOnBack) {
			setMessageErrorForm('');
		}
	}, [canSaveModuleOnBack]);

	return (
		<DynamicPageContext.Provider
			value={{
				isLoad,
				setIsLoad,
				isFormOpen,
				setIsFormOpen,
				dynamicPagesList,
				setDynamicPagesList,
				listOptions,
				backupListOptions,
				setBackupLisOptions,
				setListOptions,
				dynamicPageSelected,
				setDynamicPageSelected,
				itensOnPhone,
				setItensOnPhone,
				setTypeItemToAdd,
				typeItemToAdd,
				moduleOnPhoneToEdit,
				setModuleOnPhoneToEdit,
				moduleInSavingMode,
				setModuleInSavingMode,
				canSaveModuleOnBack,
				setCanSaveModuleOnBack,
				messageErrorForm,
				setMessageErrorForm,
				itensOnPhoneBackup,
				setItensOnPhoneBackup
			}}
		>
			{children}
		</DynamicPageContext.Provider>
	);
}

export default DynamicPageContext;
